import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface EmailConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMarketing?: boolean;
  initialProduct?: boolean;
  initialExpiration?: boolean;
}

export const EmailConsentModal: React.FC<EmailConsentModalProps> = ({
  isOpen,
  onClose,
  initialMarketing = false,
  initialProduct = false,
  initialExpiration = false
}) => {
  const { updateEmailConsent } = useAuth();
  const { showToast } = useToast();
  // No marketing UI toggle anymore; carry the initial value through to the
  // save call unchanged so the backend contract and stored value are intact.
  const [marketingConsent] = useState(initialMarketing);
  const [productConsent, setProductConsent] = useState(initialProduct);
  const [expirationConsent, setExpirationConsent] = useState(initialExpiration);
  const [saving, setSaving] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const success = await updateEmailConsent(marketingConsent, productConsent, expirationConsent);
      if (success) {
        showToast('Email preferences saved successfully', 'success');
        onClose();
      } else {
        showToast('Failed to save preferences. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error saving email consent:', error);
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          {/* Backdrop — intentionally inert; user must Save to dismiss */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="relative z-10 w-full max-w-md my-auto bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-soundwave-600 to-soundwave-500 px-4 py-3 sm:px-6 sm:py-5 flex-shrink-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Email Preferences</h2>
              </div>
            </div>

            {/* Body - Scrollable */}
            <div className="overflow-y-auto flex-1 px-4 py-4 sm:p-6 space-y-4 sm:space-y-6">
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                Choose what emails you'd like to receive — change these anytime.
              </p>

              {/* Transactional Emails (Always On) */}
              <div className="p-3 sm:p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="mt-0.5">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded bg-gray-600 flex items-center justify-center">
                      <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-xs sm:text-sm font-semibold text-gray-300">Transactional Emails</h3>
                      <span className="px-1.5 py-0.5 bg-gray-700 rounded text-[10px] sm:text-xs text-gray-400">Required</span>
                    </div>
                    <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed">
                      Account, security, and upload notifications — essential, so these can't be turned off.
                    </p>
                  </div>
                </div>
              </div>

              {/* Expiration Reminders */}
              <div className="p-3 sm:p-4 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                <label className="flex items-start gap-2 sm:gap-3 cursor-pointer group">
                  <div className="mt-0.5">
                    <input
                      type="checkbox"
                      checked={expirationConsent}
                      onChange={(e) => setExpirationConsent(e.target.checked)}
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-white/30 bg-white/5 text-soundwave-500 focus:ring-2 focus:ring-soundwave-500/50 focus:ring-offset-0 transition-all cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xs sm:text-sm font-semibold text-white group-hover:text-soundwave-400 transition-colors mb-1">
                      File Expiration Reminders
                    </h3>
                    <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed">
                      A heads-up before your files are deleted, so you can download or keep them in time.
                    </p>
                  </div>
                </label>
              </div>

              {/* Product Updates */}
              <div className="p-3 sm:p-4 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                <label className="flex items-start gap-2 sm:gap-3 cursor-pointer group">
                  <div className="mt-0.5">
                    <input
                      type="checkbox"
                      checked={productConsent}
                      onChange={(e) => setProductConsent(e.target.checked)}
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-white/30 bg-white/5 text-soundwave-500 focus:ring-2 focus:ring-soundwave-500/50 focus:ring-offset-0 transition-all cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xs sm:text-sm font-semibold text-white group-hover:text-soundwave-400 transition-colors mb-1">
                      Product Updates
                    </h3>
                    <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed">
                      Occasional news about new features and important changes to PasteWaves.
                    </p>
                  </div>
                </label>
              </div>

              {/* Privacy Note */}
              <div className="p-2.5 sm:p-3 bg-neon-500/10 border border-neon-500/30 rounded-lg">
                <p className="text-[11px] sm:text-xs text-neon-400 leading-relaxed">
                  <strong>Privacy Promise:</strong> We'll never sell your email address to third parties. You can unsubscribe or change these preferences at any time from your profile settings.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 sm:px-6 sm:py-4 bg-white/5 border-t border-white/10 flex items-center justify-end gap-2 sm:gap-3 flex-shrink-0">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-soundwave-600 to-soundwave-500 hover:from-soundwave-500 hover:to-soundwave-400 rounded-lg shadow-lg shadow-soundwave-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Save Preferences</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
